import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useGetSeasonSponsors from '../../hooks/getSeasonSponsors';
import { UpcomingSeasonsCard } from './RegistrationPromoCard';

function ProfileSidebarRight({ activeLeague, activeSeason, onVisibilityChange }) {
    const { sponsors, loading: sponsorsLoading, error } = useGetSeasonSponsors(activeSeason);
    const [openSeasons, setOpenSeasons] = useState([]);
    const [playerIds, setPlayerIds] = useState([]);
    const [promosLoading, setPromosLoading] = useState(true);
    const componentRef = useRef(null);
    const prevVisibilityRef = useRef(null);
    const initialLoadRef = useRef(true);

    useEffect(() => {
        let cancelled = false;

        const loadPromos = async () => {
            setPromosLoading(true);
            try {
                const [seasonsResponse, detailsResponse] = await Promise.all([
                    axios.get('/api/leagues/seasons/open_registrations/', {
                        withCredentials: true,
                    }),
                    axios.get('/api/users/my-details/', {
                        withCredentials: true,
                    }),
                ]);
                if (cancelled) return;
                setOpenSeasons(Array.isArray(seasonsResponse.data) ? seasonsResponse.data : []);
                const players = detailsResponse.data?.players || [];
                setPlayerIds(players.map((player) => player.id));
            } catch (loadError) {
                if (cancelled) return;
                console.error('Error loading registration promos:', loadError);
                setOpenSeasons([]);
            } finally {
                if (!cancelled) {
                    setPromosLoading(false);
                }
            }
        };

        loadPromos();
        return () => {
            cancelled = true;
        };
    }, []);

    const playerIdSet = new Set(playerIds.map(Number));
    const promoSeasons = openSeasons.filter((season) => {
        if (!season?.registration_open) return false;
        const participants = season.participants || [];
        const alreadyRegistered = participants.some((participant) =>
            playerIdSet.has(Number(participant.id))
        );
        return !alreadyRegistered;
    });

    const hasSponsors = !error && sponsors.length > 0;
    const hasPromos = promoSeasons.length > 0;
    const stillLoading = promosLoading || sponsorsLoading;
    const shouldHide = !stillLoading && !hasSponsors && !hasPromos;
    const isVisible = !shouldHide;

    useEffect(() => {
        const prevVisibility = prevVisibilityRef.current;

        if (prevVisibility !== isVisible && onVisibilityChange && activeSeason && !initialLoadRef.current) {
            onVisibilityChange(isVisible);
            prevVisibilityRef.current = isVisible;
        }

        if (activeSeason && initialLoadRef.current && !stillLoading) {
            initialLoadRef.current = false;
            if (onVisibilityChange) {
                onVisibilityChange(isVisible);
                prevVisibilityRef.current = isVisible;
            }
        }
    }, [isVisible, onVisibilityChange, activeSeason, stillLoading]);

    return (
        <div ref={componentRef} className={`col-lg-3 pe-0 pe-lg-4 ${shouldHide ? 'd-none' : ''}`}>
            <div className="row mt-sm-0 mt-3">
                <div className="col-12">
                    {hasPromos && <UpcomingSeasonsCard seasons={promoSeasons} />}

                    {hasSponsors && (
                        <div className="card">
                            <div className="card-header pb-0 border-0">
                                <h2 className="h5 card-title mb-0">Season Sponsors</h2>
                            </div>
                            <div className="card-body">
                                {sponsors.map((sponsor) => (
                                    <div key={sponsor.id} className="mb-3">
                                        <div className="d-flex align-items-center">
                                            {sponsor.logo_url && (
                                                <img
                                                    src={sponsor.logo_url}
                                                    alt={`${sponsor.name} logo`}
                                                    className="me-2"
                                                    style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                            )}
                                            <div className="flex-grow-1">
                                                <h3 className="h6 mb-0">
                                                    {sponsor.website ? (
                                                        <a
                                                            href={sponsor.website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                            {sponsor.name}
                                                        </a>
                                                    ) : (
                                                        sponsor.name
                                                    )}
                                                </h3>
                                                {sponsor.tier_name && (
                                                    <small className="text-muted">{sponsor.tier_name}</small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProfileSidebarRight;
